import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/nextjs'

const REDACTED = '[REDACTED]'
const REDACTED_EMAIL = '[REDACTED_EMAIL]'
const REDACTED_PHONE = '[REDACTED_PHONE]'
const REDACTED_SSN = '[REDACTED_SSN]'
const REDACTED_JWT = '[REDACTED_JWT]'
const REDACTED_BEARER = 'Bearer [REDACTED]'

const SENSITIVE_KEYS = new Set([
  'authorization',
  'cookie',
  'cookies',
  'password',
  'passwd',
  'secret',
  'token',
  'access_token',
  'refresh_token',
  'session_token',
  'api_key',
  'apikey',
  'x_api_key',
  'x_auth_token',
  'email',
  'phone',
  'ssn',
  'tax_id',
  'taxid',
  'card_number',
  'cvv',
])

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
const PHONE_REGEX = /\b(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\b/g
const SSN_REGEX = /\b\d{3}-?\d{2}-?\d{4}\b/g
const JWT_REGEX = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\b/g
const BEARER_REGEX = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key)

  for (const candidate of SENSITIVE_KEYS) {
    if (normalized === normalizeKey(candidate)) {
      return true
    }
  }

  return false
}

function redactString(input: string): string {
  return input
    .replace(BEARER_REGEX, REDACTED_BEARER)
    .replace(JWT_REGEX, REDACTED_JWT)
    .replace(EMAIL_REGEX, REDACTED_EMAIL)
    .replace(PHONE_REGEX, REDACTED_PHONE)
    .replace(SSN_REGEX, REDACTED_SSN)
}

function scrubValue(value: unknown, parentKey?: string): unknown {
  if (value == null) {
    return value
  }

  if (typeof value === 'string') {
    return parentKey && isSensitiveKey(parentKey) ? REDACTED : redactString(value)
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return parentKey && isSensitiveKey(parentKey) ? REDACTED : value
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, parentKey))
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {}

    for (const [key, child] of Object.entries(value)) {
      result[key] = isSensitiveKey(key) ? REDACTED : scrubValue(child, key)
    }

    return result
  }

  return value
}

export function redactSentryEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent {
  const scrubbed = scrubValue(event) as ErrorEvent

  if (scrubbed.user) {
    delete scrubbed.user.email
    delete scrubbed.user.ip_address
    if (typeof scrubbed.user.username === 'string') {
      scrubbed.user.username = redactString(scrubbed.user.username)
    }
  }

  return scrubbed
}

export function redactSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubValue(breadcrumb) as Breadcrumb
}
